/**
 * Motor de sugerencias (§6).
 *
 * Todo lo que sale de acá es **una propuesta editable, nunca un valor impuesto**. El usuario
 * define su rutina; la app opina. El tipo `Sugerencia` lleva `editable: true` como
 * recordatorio de que la UI no tiene permiso para presentar esto como una orden.
 */

import { UMBRAL_SOLAPAMIENTO, nombreMusculo, solapamientoEntreDias } from './musculos.ts';
import type { Ejercicio, Maquina, Musculo, Serie, SerieContexto, Unidad } from './types.ts';
import { formatearNumero, incrementoDe, siguienteSalto } from './units.ts';
import { detectarEstancamiento, ultimaMarca, type UltimaMarca } from './kpis/fuerza.ts';

export type TipoSugerencia = 'primera_vez' | 'subir' | 'mantener' | 'cambiar_variable' | 'bajar';

export interface Sugerencia {
  tipo: TipoSugerencia;
  /** null en 'primera_vez': §6.2 prohíbe dar un número absoluto sin historial. */
  pesoSugerido: number | null;
  unidad: Unidad;
  repsSugeridas: number | null;
  incremento: number | null;
  /** Por qué. La sugerencia sin el criterio es un número mágico. */
  motivo: string;
  /** Siempre true: la UI debe permitir cambiarlo. */
  editable: true;
  /** Marca lo que la última vez quedó registrado, para que el usuario compare. */
  ultimaVez: {
    peso: number;
    unidad: Unidad;
    reps: number[];
    fecha: string;
  } | null;
}

export interface EntradaSugerencia {
  ejercicio: Ejercicio;
  historial: SerieContexto[];
  maquina?: Maquina | null;
  /** Sobrescribe el rango del ejercicio, si la rutina define otro. */
  rangoReps?: [number, number] | null;
  minimoEstancamiento?: number;
}

const CRITERIO_PESO_INICIAL =
  'La última repetición tiene que costar pero salir limpia. Si podés hacer dos más, es poco; ' +
  'si se te rompe la técnica, es mucho.';

/**
 * §6.1 — al abrir un ejercicio se muestra la última marca y una sugerencia:
 * - completó todas las series al tope del rango → subir un incremento de placa;
 * - no llegó al mínimo del rango → mantener;
 * - 3 sesiones estancado → cambiar la variable antes que insistir con el mismo peso.
 */
export function sugerirCarga(e: EntradaSugerencia): Sugerencia {
  const rango = e.rangoReps ?? e.ejercicio.rango_reps;
  const marca = ultimaMarca(e.historial, e.ejercicio.id);
  const unidad: Unidad = marca?.referencia.unidad ?? e.maquina?.unidad_placa ?? 'kg';
  const incremento = incrementoDe(e.maquina, unidad);

  if (!marca) return sugerenciaPrimeraVez(e.ejercicio, rango, unidad, incremento);

  const ultimaVez = {
    peso: marca.referencia.peso,
    unidad: marca.referencia.unidad,
    reps: marca.series.map((s) => s.reps),
    fecha: marca.fecha,
  };

  const estancado = detectarEstancamiento(e.historial, e.ejercicio.id, e.minimoEstancamiento ?? 3);
  if (estancado) {
    return {
      tipo: 'cambiar_variable',
      pesoSugerido: marca.referencia.peso,
      unidad,
      repsSugeridas: rango ? rango[1] + 1 : null,
      incremento,
      motivo:
        `Llevás ${estancado.sesiones} sesiones con el mismo peso y las mismas reps. Insistir con ` +
        `el mismo estímulo no lo cambia: probá una serie más, o sumar reps, o bajar el ritmo de ` +
        `la fase excéntrica, antes de volver a tocar la placa.`,
      editable: true,
      ultimaVez,
    };
  }

  if (!rango) {
    return {
      tipo: 'mantener',
      pesoSugerido: marca.referencia.peso,
      unidad,
      repsSugeridas: marca.referencia.reps,
      incremento,
      motivo: `Sin rango de reps definido para este ejercicio, repetimos lo último: ${formatearNumero(marca.referencia.peso)} ${unidad} × ${marca.referencia.reps}.`,
      editable: true,
      ultimaVez,
    };
  }

  const [minReps, maxReps] = rango;
  const completadas = marca.series.filter((s) => s.completada);

  if (completadas.length === 0) {
    return {
      tipo: 'mantener',
      pesoSugerido: marca.referencia.peso,
      unidad,
      repsSugeridas: minReps,
      incremento,
      motivo: 'La última vez este ejercicio quedó sin series completadas. Retomá con el mismo peso.',
      editable: true,
      ultimaVez,
    };
  }

  const todasAlTope = completadas.every((s) => s.reps >= maxReps);
  const algunaBajoMinimo = completadas.some((s) => s.reps < minReps);
  const pesoRef = marca.referencia.peso;

  if (todasAlTope) {
    return {
      tipo: 'subir',
      pesoSugerido: siguienteSalto(pesoRef, incremento),
      unidad,
      repsSugeridas: minReps,
      incremento,
      motivo:
        `Completaste las ${completadas.length} series a ${maxReps} reps, el tope del rango. ` +
        `Subí un incremento de placa (${formatearNumero(incremento)} ${unidad}) y volvé a arrancar ` +
        `en ${minReps} reps.`,
      editable: true,
      ultimaVez,
    };
  }

  if (algunaBajoMinimo) {
    return {
      tipo: 'mantener',
      pesoSugerido: pesoRef,
      unidad,
      repsSugeridas: minReps,
      incremento,
      motivo:
        `La última vez alguna serie quedó por debajo de ${minReps} reps. Mantené ` +
        `${formatearNumero(pesoRef)} ${unidad} hasta cerrar el rango completo.`,
      editable: true,
      ultimaVez,
    };
  }

  return {
    tipo: 'mantener',
    pesoSugerido: pesoRef,
    unidad,
    repsSugeridas: Math.min(maxReps, Math.max(...completadas.map((s) => s.reps)) + 1),
    incremento,
    motivo:
      `Estás dentro del rango (${minReps}–${maxReps}). Mantené el peso y sumá una rep; cuando ` +
      `todas lleguen a ${maxReps}, subimos la placa.`,
    editable: true,
    ultimaVez,
  };
}

/**
 * §6.2 — nunca un número absoluto. Un rango y el criterio, y para un patrón nuevo
 * (sobre todo pierna) el consejo explícito de quedarse corto.
 */
function sugerenciaPrimeraVez(
  ejercicio: Ejercicio,
  rango: [number, number] | null,
  unidad: Unidad,
  incremento: number,
): Sugerencia {
  const esPierna = ['cuadriceps', 'isquios', 'gluteos', 'aductores', 'gemelos'].includes(
    ejercicio.musculo_primario,
  );

  const reps = rango ? `${rango[0]}–${rango[1]}` : '10–12';
  const aviso = esPierna
    ? ' Es la primera vez con este patrón de pierna: quedate deliberadamente corto. El dolor de una pierna mal calibrada aparece 48 h después, y te cuesta la sesión siguiente.'
    : ' Es la primera vez con este ejercicio: quedate corto en la primera serie y ajustá en la segunda.';

  return {
    tipo: 'primera_vez',
    pesoSugerido: null,
    unidad,
    repsSugeridas: rango?.[0] ?? null,
    incremento,
    motivo:
      `Sin historial no hay número que dar. Elegí un peso con el que puedas hacer ${reps} reps ` +
      `donde la última cueste pero salga limpia.${aviso} ${CRITERIO_PESO_INICIAL}`,
    editable: true,
    ultimaVez: null,
  };
}

// ---------------------------------------------------------------------------
// §6.3 — recuperación y solapamiento
// ---------------------------------------------------------------------------

export interface ReordenSugerido {
  /** Ejercicios en el orden propuesto. */
  orden: Ejercicio[];
  musculosEnConflicto: Musculo[];
  score: number;
  motivo: string;
  /** true si el orden propuesto difiere del actual. */
  cambia: boolean;
}

/**
 * Cuando la sesión de hoy carga un músculo que ya se cargó ayer, la recomendación de §6.3 es
 * reordenar para poner el ejercicio afectado primero, con el músculo todavía fresco.
 */
export function sugerirReorden(
  ejerciciosHoy: Ejercicio[],
  ejerciciosAyer: Ejercicio[],
  umbral = UMBRAL_SOLAPAMIENTO,
): ReordenSugerido | null {
  if (ejerciciosHoy.length === 0 || ejerciciosAyer.length === 0) return null;

  const { score, musculos } = solapamientoEntreDias(ejerciciosHoy, ejerciciosAyer);
  if (score < umbral || musculos.length === 0) return null;

  const afectados = new Set(musculos);
  const puntaje = (ej: Ejercicio): number => {
    if (afectados.has(ej.musculo_primario)) return 2;
    if (ej.musculos_secundarios.some((m) => afectados.has(m))) return 1;
    return 0;
  };

  const orden = [...ejerciciosHoy].sort((a, b) => puntaje(b) - puntaje(a));
  const cambia = orden.some((ej, i) => ej.id !== ejerciciosHoy[i]!.id);
  const primero = orden[0]!;

  return {
    orden,
    musculosEnConflicto: musculos,
    score,
    cambia,
    motivo: cambia
      ? `${musculos.map(nombreMusculo).join(', ')} ya trabajó ayer. Poné ${primero.nombre} primero, mientras el músculo está fresco.`
      : `${musculos.map(nombreMusculo).join(', ')} ya trabajó ayer, pero el orden actual ya pone primero lo afectado.`,
  };
}

/**
 * Series a arrastrar a la siguiente sesión (§4.3): las omitidas por causas ajenas al
 * usuario. Las omitidas por decisión propia no se arrastran solas — sería insistir con algo
 * que ya se descartó.
 */
export function seriesParaArrastrar(ctxs: SerieContexto[], sesionId: string): Serie[] {
  return ctxs
    .filter(
      (c) =>
        c.sesion_id === sesionId &&
        !c.serie.completada &&
        (c.serie.motivo_omision === 'equipo_ocupado' || c.serie.motivo_omision === 'sin_tiempo'),
    )
    .map((c) => c.serie);
}

export type { UltimaMarca };
